/**
 * WANLE-2 · 渠道中心 › 携程玩乐 › 「连接与凭据」里的凭据卡（样稿 携程玩乐直连-样稿.html ⑦）。
 * accountId / 签名密钥 / AES key / iv 加密存，只显示「已配置」，不回显；我方接收地址给携程填「商家接口地址」。
 *   PUT /api/v1/integrations/platforms/ctrip_wanle/wanle-credentials
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/services/api";
import { wanleApi } from "@/services/ctripWanle";

const inp = "w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 font-mono text-[13px]";

type Urls = { order_notice_url: string | null; price_url: string | null; stock_url: string | null };
const URL_ROWS = [["order_notice_url", "订单对接地址"], ["price_url", "价格同步地址"], ["stock_url", "库存同步地址"]] as const;

export function WanleCredentialsCard({ supplierId, configured, urls, inboundUrl, environment, canEdit, queryKeys }: {
  supplierId: string | null; configured: boolean; urls?: Urls | null; inboundUrl: string | null; environment: "sandbox" | "production"; canEdit: boolean; queryKeys: unknown[][];
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ account_id: supplierId ?? "", signkey: "", aes_key: "", aes_iv: "" });
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => wanleApi.saveCredentials({ account_id: f.account_id.trim(), signkey: f.signkey.trim(), aes_key: f.aes_key.trim(), aes_iv: f.aes_iv.trim() }),
    onSuccess: () => { setOpen(false); setErr(null); setF((x) => ({ ...x, signkey: "", aes_key: "", aes_iv: "" })); queryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k })); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "保存失败，请稍后再试"),
  });
  const [uOpen, setUOpen] = useState(false);
  const [uf, setUf] = useState<Urls>({ order_notice_url: urls?.order_notice_url ?? "", price_url: urls?.price_url ?? "", stock_url: urls?.stock_url ?? "" } as Urls);
  const [uErr, setUErr] = useState<string | null>(null);
  const saveUrls = useMutation({
    mutationFn: () => wanleApi.saveUrls({ order_notice_url: (uf.order_notice_url ?? "").trim(), price_url: (uf.price_url ?? "").trim(), stock_url: (uf.stock_url ?? "").trim() }),
    onSuccess: () => { setUOpen(false); setUErr(null); queryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k })); },
    onError: (e) => setUErr(e instanceof ApiError ? e.message : "保存失败，请稍后再试"),
  });
  const bad = !f.account_id.trim() || !f.signkey.trim() || f.aes_key.trim().length !== 16 || f.aes_iv.trim().length !== 16;
  return (
    <section data-testid="wanle-creds" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-1 text-sm font-semibold text-slate-900">携程玩乐凭据</h2>
      <p className="mb-3 text-[12px] text-slate-500">和「携程」团队游分开：不同合同、不同凭据。四件套在携程沙箱「查看接口参数」的邮件里。</p>
      <div className="grid grid-cols-[160px_1fr] items-center gap-x-3 gap-y-2.5 text-[13px]">
        <span className="text-slate-500">Account ID</span>
        <span>{supplierId ? <span className="font-mono">{supplierId}</span> : <span className="text-slate-400">未配置</span>}</span>
        <span className="text-slate-500">签名密钥 / 加密 Key / IV</span>
        <span className="flex items-center gap-2">
          <span data-testid="wanle-creds-state" className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{configured ? "已配置" : "未配置"}</span>
          <span className="text-[12px] text-slate-400">只显示是否配置，不回显</span>
          {canEdit && <button type="button" data-testid="wanle-creds-edit" onClick={() => setOpen(true)} className="ml-auto rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">{configured ? "更新" : "配置"}</button>}
        </span>
        <span className="text-slate-500">我方接收地址</span>
        <span className="break-all font-mono text-[12px]">{inboundUrl ? `${window.location.origin}${inboundUrl}/` : "—"}</span>
        {URL_ROWS.map(([k, label]) => (
          <span key={k} className="contents">
            <span className="text-slate-500">{label}</span>
            <span data-testid={`wanle-url-${k}`} className="break-all font-mono text-[12px]">{urls?.[k] ? urls[k] : <span className="font-sans text-slate-400">未配置</span>}</span>
          </span>
        ))}
        <span className="text-slate-500" />
        <span className="flex items-center gap-2">
          <span className="text-[12px] text-slate-400">沙箱 / 生产地址不同，上线时要换；必须 https，域名为 ctrip.com 或 ctripqa.com</span>
          {canEdit && <button type="button" data-testid="wanle-urls-edit" onClick={() => { setUf({ order_notice_url: urls?.order_notice_url ?? "", price_url: urls?.price_url ?? "", stock_url: urls?.stock_url ?? "" }); setUErr(null); setUOpen(true); }} className="ml-auto shrink-0 rounded-md border border-slate-200 px-2.5 py-1 text-[12px] font-medium text-slate-700 hover:bg-slate-50">修改地址</button>}
        </span>
        <span className="text-slate-500">环境</span>
        <span className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${environment === "sandbox" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{environment === "sandbox" ? "沙箱" : "生产"}</span>
          <span className="text-[12px] text-slate-400">沙箱测试用例跑满 100% 后再申请上线</span>
        </span>
      </div>
      {uOpen && (
        <div role="dialog" aria-label="修改携程玩乐回调地址" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <div className="w-[520px] max-w-[92vw] space-y-3 rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-[15px] font-semibold text-slate-900">修改携程玩乐回调地址</h3>
            <p className="text-[12px] text-slate-500">我们主动调携程的三个固定地址（接口名只在报文头里，地址里不带）。留空 = 清空；沙箱 / 生产不同，上线时要换成正式地址。</p>
            {URL_ROWS.map(([k, label]) => (
              <label key={k} className="block text-[12.5px] text-slate-600">{label}
                <input aria-label={label} type="url" autoComplete="off" placeholder="https://….ctrip.com/…" value={uf[k] ?? ""} onChange={(e) => setUf((x) => ({ ...x, [k]: e.target.value }))} className={inp} />
              </label>
            ))}
            {uErr && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{uErr}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setUOpen(false); setUErr(null); }} className="rounded-md border border-slate-200 px-3 py-1.5 text-[13px] text-slate-700">取消</button>
              <button type="button" data-testid="wanle-urls-save" disabled={saveUrls.isPending} onClick={() => saveUrls.mutate()} className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50">保存</button>
            </div>
          </div>
        </div>
      )}
      {open && (
        <div role="dialog" aria-label="更新携程玩乐凭据" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
          <div className="w-[460px] max-w-[92vw] space-y-3 rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-[15px] font-semibold text-slate-900">更新携程玩乐凭据</h3>
            <p className="text-[12px] text-slate-500">四项一起填；保存后只显示「已配置」，旧值不可回读。</p>
            {([["account_id", "Account ID"], ["signkey", "签名密钥 signkey"], ["aes_key", "AES Key（16 位）"], ["aes_iv", "AES IV（16 位）"]] as const).map(([k, label]) => (
              <label key={k} className="block text-[12.5px] text-slate-600">{label}
                <input aria-label={label} type={k === "account_id" ? "text" : "password"} autoComplete="off" value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} className={inp} />
              </label>
            ))}
            {err && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700">{err}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setOpen(false); setErr(null); }} className="rounded-md border border-slate-200 px-3 py-1.5 text-[13px] text-slate-700">取消</button>
              <button type="button" data-testid="wanle-creds-save" disabled={bad || save.isPending} onClick={() => save.mutate()} className="rounded-md bg-blue-600 px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-50">保存</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
